/**
 * States of the page in view that a developer can step through from the dev
 * toolbar — its `scenarios` prop. The shapes mirror agentation-svelte's
 * `Scenario` and `ScenarioSource`; they are restated here because the package
 * is optional and may not be installed.
 *
 * A page provides its scenarios on mount and withdraws them on destroy; the
 * root layout hands the toolbar `pageScenarios`, which reads whatever page is
 * current.
 */

export interface Scenario {
	id: string;
	label: string;
	description?: string;
	steps: { id: string; label: string; description?: string }[];
	defaultStepId: string;
	activeStepId: string;
	reloads?: boolean;
}

export interface ScenarioSource {
	list(url: URL): Scenario[];
	select(scenarioId: string, stepId: string): void;
}

let current: ScenarioSource | null = null;
const listeners = new Set<() => void>();

function notify() {
	for (const listener of listeners) listener();
}

/**
 * Offer the toolbar this page's scenarios. Returns the withdrawal, for the
 * page's `onMount` cleanup. Call `notify` from the returned handle's `changed`
 * whenever the active steps move for a reason the toolbar did not cause.
 */
export function providePageScenarios(source: ScenarioSource): { changed(): void; withdraw(): void } {
	current = source;
	notify();
	return {
		changed: notify,
		withdraw() {
			if (current !== source) return;
			current = null;
			notify();
		}
	};
}

export const pageScenarios = {
	list: (url: URL): Scenario[] => current?.list(url) ?? [],
	select: (scenarioId: string, stepId: string) => current?.select(scenarioId, stepId),
	subscribe(listener: () => void) {
		listeners.add(listener);
		return () => listeners.delete(listener);
	}
};
